import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { ROUTES } from '@/shared/config/routes';

/**
 * Router-level error boundary. Catches render/loader errors (including a failed
 * lazy chunk fetch after a redeploy) so the app degrades to a readable page
 * instead of a blank screen.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'Unexpected error';

  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center">
      <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
      <h1 className="mt-4 font-display text-2xl font-bold text-foreground">Something broke</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This page couldn’t be rendered. Reloading usually fixes it — the app may have been updated
        while you were reading.
      </p>
      <p className="mt-3 break-words font-mono text-[10px] text-muted-foreground">{detail}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-zinc-950 transition-all hover:bg-amber-300"
        >
          Reload
        </button>
        <Link
          to={ROUTES.home}
          className="rounded-full border border-border px-5 py-2.5 text-sm font-bold text-foreground transition-colors hover:border-zinc-400"
        >
          Back to articles
        </Link>
      </div>
    </div>
  );
}
